import {
  AgentProfile,
  GuestProfile,
  LimitedAgentProfile,
  Phone
} from "@closerplatform/spinner-openapi";
import { intl } from "../i18n/TypedIntl";
import { isAgentProfile, isGuestProfile, UserProfile } from "../models/userProfile";
import { logger } from "./logger";

export abstract class UserPresenter {
  public abstract readonly displayedName: string;
  public abstract readonly firstName: string | undefined;

  static from(user: UserProfile, currentUser?: boolean): AgentPresenter | UserPresenter {
    if (isAgentProfile(user)) {
      return new AgentPresenter(user, currentUser);
    }
    if (isGuestProfile(user)) {
      return new GuestPresenter(user, currentUser);
    }
    logger.warn("unsupported user profile", user);
    return new AgentPresenter(user, currentUser);
  }
}

type AgentPresenterProfile = AgentProfile | LimitedAgentProfile;
export class AgentPresenter extends UserPresenter {
  constructor(
    private readonly profile: AgentPresenterProfile,
    private readonly currentUser?: boolean
  ) {
    super();
  }

  get displayedName(): string {
    return this.currentUser
      ? `${this.profile.firstName} (${intl.formatMessage("user.displayedName.you")})`
      : this.getFullName();
  }

  get firstName(): string | undefined {
    return this.profile.firstName;
  }

  private getFullName(): string {
    if ("orgRole" in this.profile) {
      return `${this.profile.firstName} ${this.profile.lastName}`.trim();
    }
    return `${this.profile.firstName}`.trim();
  }
}

type GuestPresenterProfile = Pick<
  GuestProfile,
  "phone" | "id" | "firstName" | "lastName" | "randomName"
>;
export class GuestPresenter extends UserPresenter {
  constructor(
    private readonly profile: GuestPresenterProfile,
    private readonly currentUser?: boolean
  ) {
    super();
  }

  get displayedName(): string {
    return this.currentUser
      ? intl.formatMessage("user.displayedName.you")
      : (this.profile.firstName &&
          this.profile.lastName &&
          `${this.profile.firstName} ${this.profile.lastName}`) ||
          this.profile.lastName ||
          this.profile.firstName ||
          this.profile.randomName ||
          this.getAnonymousLabel(this.profile.id);
  }

  get firstName(): string {
    return this.profile.firstName || this.fakeFirstName || this.getAnonymousLabel(this.profile.id);
  }

  get fakeFirstName(): string {
    return (
      (this.profile.randomName && this.profile.randomName.split(" ")[0]) ||
      this.getAnonymousLabel(this.profile.id)
    );
  }

  get fakeLastName(): string {
    return (
      (this.profile.randomName && this.profile.randomName.split(" ")[1]) ||
      this.getAnonymousLabel(this.profile.id)
    );
  }

  get phone(): Phone | undefined {
    return this.profile?.phone;
  }

  private getAnonymousLabel(id: string): string {
    return intl.formatMessage("user.displayedName.anonymousClient", { id });
  }
}
